import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Checkbox, FormControlLabel, Paper, Alert, CircularProgress, useMediaQuery, useTheme, IconButton, Stack, Tooltip, Chip,
  Grid, Card, CardContent, Avatar, InputAdornment, Divider, MenuItem
} from '@mui/material';
import { Add, Edit, Delete, ToggleOn, ToggleOff, Phone, Work, Person, Badge, Star } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { maintenanceStaffAPI, usersAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const SPECIALIZATION_OPTIONS = [
  { value: 'SUPERVISOR', label: 'Supervisor' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'SECURITY_GUARD', label: 'Security Guard' },
  { value: 'PLUMBER', label: 'Plumber' },
  { value: 'ELECTRICIAN', label: 'Electrician' },
  { value: 'CLEANER', label: 'Cleaner' },
  { value: 'HOUSEKEEPING', label: 'Housekeeping' },
  { value: 'GENERAL_MAINTENANCE', label: 'General Maintenance' },
  { value: 'CARPENTER', label: 'Carpenter' },
  { value: 'HVAC_TECHNICIAN', label: 'HVAC Technician' },
  { value: 'LIFT_TECHNICIAN', label: 'Lift Technician' },
  { value: 'GARDENER', label: 'Gardener' },
  { value: 'PEST_CONTROL', label: 'Pest Control' },
  { value: 'OTHER', label: 'Other' }
];

const getSpecializationLabel = value => SPECIALIZATION_OPTIONS.find(option => option.value === value)?.label
  || value?.replaceAll('_', ' ')
  || '-';

const EmployeeDetails = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.userId || user?.attributes?.sub || user?.username;

  const { data: buildingId, isLoading: isBuildingLoading, isError: isBuildingError } = useQuery(
    ['employee-details-building', currentUserId],
    async () => {
      const res = await usersAPI.getAssociations(currentUserId);
      const associations = Array.isArray(res.data) ? res.data : [];
      return associations.find(item => item?.buildingId)?.buildingId || null;
    },
    { enabled: Boolean(currentUserId) }
  );

  // Fetch staff list — normalize to an array to avoid cached axios response object
  const { data: staff = [], isLoading, isError } = useQuery(['maintenance-staff', 'list', buildingId], async () => {
    const res = await maintenanceStaffAPI.getAll(buildingId);
    return Array.isArray(res.data) ? res.data : (res.data?.items || []);
  }, { enabled: Boolean(buildingId) });

  // Login linkage is optional. Only active maintenance application users from
  // this building are offered, so admins never need to copy database IDs.
  const { data: maintenanceUsers = [], isLoading: isUsersLoading, isError: isUsersError } = useQuery(
    ['employee-details-maintenance-users', buildingId],
    async () => {
      const res = await usersAPI.getUsers(buildingId, {
        userType: 'MAINTENANCE_STAFF',
        activeOnly: true,
        includeFlats: false
      });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.users || []);
      return rows.filter(candidate =>
        String(candidate?.userType || '').toUpperCase() === 'MAINTENANCE_STAFF' && candidate?.isActive !== false
      );
    },
    { enabled: Boolean(buildingId) }
  );

  const linkedUserIds = React.useMemo(
    () => new Set((staff || []).map(member => member.userId).filter(Boolean)),
    [staff]
  );

  // Dialog state
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const createMutation = useMutation((newStaff) => maintenanceStaffAPI.create({ ...newStaff, buildingId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-staff','list']);
      setOpen(false);
      setForm({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
      setErrorMsg('');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err.message || t('employeeDetails.addFailed');
      setErrorMsg(msg);
    }
  });

  const updateMutation = useMutation(({ id, data }) => maintenanceStaffAPI.update(id, data, buildingId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['maintenance-staff','list']);
      setOpen(false);
      setForm({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
      setErrorMsg('');
      setIsEditing(false);
      setEditingId(null);
    },
    onError: (err) => setErrorMsg(err?.response?.data?.message || err.message || t('employeeDetails.updateFailed'))
  });

  const deleteMutation = useMutation((id) => maintenanceStaffAPI.delete(id, buildingId), {
    onSuccess: () => queryClient.invalidateQueries('maintenance-staff'),
    onError: (err) => setErrorMsg(err?.response?.data?.message || err.message || t('employeeDetails.deleteFailed'))
  });

  const [isEditing, setIsEditing] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);

  const handleOpen = (e) => {
    // Ensure the element that opened the dialog does not retain focus
    // because MUI sets `aria-hidden` on the app root when mounting the dialog.
    // If a focused descendant remains, the browser will block the aria-hidden change.
    try {
      if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
        e.currentTarget.blur();
      } else if (document && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (err) {
      // swallow any errors — this is a best-effort accessibility fix
    }
    setOpen(true);
    setErrorMsg('');
    setFieldErrors({});
    setIsEditing(false);
    setEditingId(null);
    setForm({ name: '', userId: '', phoneNumber: '', specialization: '', isAvailable: true, rating: '' });
  };
  const handleOpenForEdit = (member) => {
    setForm({
      name: member.name || '',
      userId: member.userId || '',
      phoneNumber: member.phoneNumber || '',
      specialization: member.specialization || '',
      isAvailable: !!member.isAvailable,
      rating: member.rating || ''
    });
    setIsEditing(true);
    setEditingId(member.staffId || member.userId);
    setOpen(true);
    setErrorMsg('');
    setFieldErrors({});
  };

  // Auto-open edit dialog when ?id= is present in URL
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    if (!id || !buildingId) return;

    // If staff list already contains the member, open immediately
    const found = (staff || []).find(s => (s.staffId === id) || (s.userId === id));
    if (found) {
      handleOpenForEdit(found);
      // remove query param
      params.delete('id');
      navigate({ search: params.toString() }, { replace: true });
      return;
    }

    // Otherwise fetch by id and open
    (async () => {
      try {
        const res = await maintenanceStaffAPI.getById(id, buildingId);
        const member = Array.isArray(res.data) ? res.data[0] : res.data;
        if (member) handleOpenForEdit(member);
      } catch (err) {
        console.error('Failed to fetch staff by id from query param', err);
      } finally {
        params.delete('id');
        navigate({ search: params.toString() }, { replace: true });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, staff, buildingId]);
  const handleClose = () => { setOpen(false); setErrorMsg(''); setFieldErrors({}); };

  // Group staff by specialization for headered list
  const groupedBySpecialization = React.useMemo(() => {
    const map = {};
    (staff || []).forEach(s => {
      const key = s.specialization && s.specialization.trim() ? s.specialization.trim() : t('employeeDetails.general');
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [staff, t]);

  const handleChange = (field) => (e) => {
    setFieldErrors(prev => ({ ...prev, [field]: '' }));
    setForm(prev => ({ ...prev, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    const errors = {};
    if (!form.name.trim()) {
      errors.name = t('employeeDetails.nameRequired');
    } else if (!/^[\p{L} ]+$/u.test(form.name.trim())) {
      errors.name = t('employeeDetails.nameInvalid');
    }
    if (!form.phoneNumber.trim()) {
      errors.phoneNumber = t('employeeDetails.phoneRequired');
    } else if (!/^\d+$/.test(form.phoneNumber.trim())) {
      errors.phoneNumber = t('employeeDetails.phoneInvalid');
    }
    if (!form.specialization) errors.specialization = 'Specialization is required for notification routing.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    // Login linkage is optional for non-login employees. When selected, the
    // dropdown supplies the actual Cognito sub as userId.
    const payload = {
      name: form.name,
      userId: form.userId || null,
      phoneNumber: form.phoneNumber,
      specialization: form.specialization,
      isAvailable: !!form.isAvailable,
      rating: form.rating ? Number(form.rating) : undefined
    };

    if (isEditing && editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      payload.isActive = true;
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id) => {
    if (!id) return;
    if (!window.confirm(t('employeeDetails.confirmDelete'))) return;
    deleteMutation.mutate(id);
  };

  const handleToggleActive = (member) => {
    const id = member.staffId || member.userId;
    if (!id) return;
    updateMutation.mutate({ id, data: { isActive: !member.isActive } });
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, pb: { xs: 'calc(104px + env(safe-area-inset-bottom, 0px))', md: 2 }, maxWidth: 1280, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{ fontWeight: 700, letterSpacing: 0 }}
          >
            {t('layout.navigation.EmployeeDetails') || 'Employee Details'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('employeeDetails.subtitle')}
          </Typography>
        </Box>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpen}
            size={isMobile ? 'medium' : 'large'}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
              minWidth: { sm: 128 },
              fontWeight: 700,
            }}
          >
            {t('employeeDetails.addStaff')}
          </Button>
        )}
      </Box>

      {isLoading || isBuildingLoading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : isError || isBuildingError || !buildingId ? (
        <Alert severity="error">{t('employeeDetails.loadFailed')}</Alert>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Grid container spacing={2}>
            {Object.keys(groupedBySpecialization).map((spec) => (
              <Grid item xs={12} md={6} xl={4} key={spec}>
              <Box
                sx={{
                  height: '100%',
                  p: { xs: 1.5, sm: 2 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.5,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="h6" sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, overflowWrap: 'break-word' }}>
                    {getSpecializationLabel(spec)}
                  </Typography>
                  <Chip
                    size="small"
                    label={t('employeeDetails.memberCount', { count: groupedBySpecialization[spec].length })}
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                  />
                </Box>
                <Grid container spacing={2}>
                  {groupedBySpecialization[spec].map((m) => (
                    <Grid item xs={12} key={m.userId || m.staffId}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderRadius: 2,
                          transition: 'box-shadow 160ms ease, transform 160ms ease',
                          '&:hover': {
                            boxShadow: 3,
                            transform: { xs: 'none', sm: 'translateY(-2px)' },
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 2.25 }, '&:last-child': { pb: { xs: 2, sm: 2.25 } } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                              <Avatar sx={{ width: { xs: 44, sm: 48 }, height: { xs: 44, sm: 48 }, bgcolor: 'primary.light', color: 'primary.contrastText', flexShrink: 0 }}>
                                <Person />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                                  {m.name || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', display: 'block' }}>
                                  {m.userId || '-'}
                                </Typography>
                              </Box>
                            </Box>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                flexShrink: 0,
                                alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                                pt: { xs: 1, sm: 0 },
                                borderTop: { xs: '1px solid', sm: 'none' },
                                borderColor: 'divider',
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                              }}
                            >
                              {!readOnly && (
                                <>
                                  <Tooltip title={t('common.edit')}>
                                    <IconButton size="small" onClick={() => handleOpenForEdit(m)} color="primary">
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={m.isActive ? t('actions.disable') : t('actions.enable')}>
                                    <IconButton size="small" onClick={() => handleToggleActive(m)}>
                                      {m.isActive ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t('common.delete')}>
                                    <IconButton size="small" onClick={() => handleDelete(m.staffId || m.userId)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Stack>
                          </Box>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                              <Phone fontSize="small" color="action" />
                              <Typography variant="body2" color="text.primary">{m.phoneNumber || '-'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                              <Work fontSize="small" color="action" />
                              <Typography variant="body2" color="text.primary" sx={{ overflowWrap: 'anywhere' }}>{getSpecializationLabel(m.specialization)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                              <Chip
                                label={m.isActive ? t('employeeDetails.active') : t('employeeDetails.disabled')}
                                color={m.isActive ? 'success' : 'default'}
                                size="small"
                                sx={{ fontWeight: 700 }}
                              />
                              <Chip
                                label={m.isAvailable ? t('employeeDetails.available') : t('employeeDetails.notAvailable')}
                                color={m.isAvailable ? 'success' : 'warning'}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            bgcolor: '#f7f9fc',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {isEditing ? t('employeeDetails.editEmployee') : t('employeeDetails.addEmployee')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('employeeDetails.dialogSubtitle')}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('employeeDetails.name')}
                  value={form.name}
                  onChange={handleChange('name')}
                  required
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Maintenance staff login (optional)"
                  value={form.userId}
                  onChange={handleChange('userId')}
                  disabled={isUsersLoading}
                  helperText={isUsersError
                    ? 'Unable to load maintenance staff users.'
                    : 'Select an active maintenance user for app login and attendance, or leave unlinked.'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value=""><em>No app login</em></MenuItem>
                  {maintenanceUsers.filter(candidate => candidate.userId || candidate.id).map(candidate => {
                    const candidateId = candidate.userId || candidate.id;
                    const fullName = [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || candidate.name || candidate.userName || 'Maintenance staff';
                    const alreadyLinked = linkedUserIds.has(candidateId) && candidateId !== form.userId;
                    return <MenuItem key={candidateId} value={candidateId} disabled={alreadyLinked}>
                      {fullName}{candidate.email ? ` · ${candidate.email}` : ''}{alreadyLinked ? ' · Already linked' : ''}
                    </MenuItem>;
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Building (automatically assigned)"
                  value={buildingId || ''}
                  disabled
                  helperText="Taken from your building-admin association."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('employeeDetails.phoneNumber')}
                  value={form.phoneNumber}
                  onChange={handleChange('phoneNumber')}
                  required
                  type="tel"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  error={Boolean(fieldErrors.phoneNumber)}
                  helperText={fieldErrors.phoneNumber}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t('employeeDetails.specialization')}
                  value={form.specialization}
                  onChange={handleChange('specialization')}
                  required
                  error={Boolean(fieldErrors.specialization)}
                  helperText={fieldErrors.specialization || 'Specialization controls operational notification routing.'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Work fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {form.specialization && !SPECIALIZATION_OPTIONS.some(option => option.value === form.specialization) && <MenuItem value={form.specialization}>{form.specialization} (existing value)</MenuItem>}
                  {SPECIALIZATION_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('employeeDetails.rating')}
                  value={form.rating}
                  onChange={handleChange('rating')}
                  type="number"
                  inputProps={{ min: 0, max: 5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Star fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: form.isAvailable ? 'rgba(46, 125, 50, 0.04)' : 'background.paper',
                  }}
                >
                  <FormControlLabel
                    control={<Checkbox checked={form.isAvailable} onChange={handleChange('isAvailable')} />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {t('employeeDetails.available')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('employeeDetails.availableHelp')}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
            <Button onClick={handleClose}>{t('common.cancel')}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isEditing ? updateMutation.isLoading : createMutation.isLoading}
              sx={{ minWidth: 112, fontWeight: 700 }}
            >
              {isEditing
                ? (updateMutation.isLoading ? t('employeeDetails.updating') : t('actions.update'))
                : (createMutation.isLoading ? t('employeeDetails.adding') : t('common.add'))}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EmployeeDetails;
